import React from "react";
import { useTranslation } from "react-i18next";
import { Send, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import {
  coerceKind,
  termsKindLabel,
  type PlatformTermsView,
} from "../../../api/General/PlatformTerms";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: PlatformTermsView | null;
  onConfirm: () => void;
}

const PublishConfirmDialog: React.FC<Props> = ({ open, onOpenChange, record, onConfirm }) => {
  const { t } = useTranslation();
  if (!record) return null;
  const kind = coerceKind(record.kind);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <Send size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Publish this version?")}</DialogTitle>
              <DialogDescription className="mt-1">
                {record.title || t(termsKindLabel(kind))}
                <span className="mx-1.5 opacity-40">·</span>v{record.version}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3 text-sm text-amber-900 dark:text-amber-200">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>
              {t("All users will be prompted to re-accept this version on next login. Continue?")}
            </span>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("Cancel")}
          </Button>
          <Button onClick={onConfirm} variant="warning">
            {t("Publish")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PublishConfirmDialog;
